import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useUser } from '../contexts/UserContextNew';
import { useRole } from '../hooks/useRole';
import { useOrganizationPermissions } from '../hooks/useOrganizationPermissions';
import { RolePermissions } from '../types/user';
import EmailVerificationDialog from './EmailVerificationDialog';
import { getEffectiveLinkageState } from '../utils/accessLinkage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireEmailVerification?: boolean;
  requirePermission?: keyof RolePermissions;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireEmailVerification = true, // Default: require email verification for all protected routes
  requirePermission,
  redirectTo = '/login',
}) => {
  const location = useLocation();
  const { user, loading: authLoading, isEmailVerified, emailVerificationLoading, effectiveUserProfile } = useUser();
  const { permissions, loading: roleLoading } = useRole();
  const { canAccessAiAnalysis, loading: orgLoading } = useOrganizationPermissions();
  const { hasSufficientAccess } = getEffectiveLinkageState(effectiveUserProfile);
  const isSuperAdmin = effectiveUserProfile?.role === 'super_admin';

  const isAllowedWhenUnlinked = (() => {
    const path = location.pathname;
    return (
      path === '/access/setup' ||
      path === '/teams' ||
      path === '/account' ||
      path.startsWith('/profile')
    );
  })();

  if (authLoading || roleLoading || orgLoading || emailVerificationLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to={redirectTo} replace />;
  }

  // SECURITY: Block unverified users from accessing protected content
  // This is a defense-in-depth measure - Firestore rules also enforce this
  if (requireAuth && requireEmailVerification && user && !isEmailVerified) {
    // Show verification dialog instead of redirecting
    return (
      <>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          flexDirection="column"
          sx={{ bgcolor: 'background.default' }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Box sx={{ textAlign: 'center', px: 2 }}>
            Please verify your email to continue.
          </Box>
        </Box>
        <EmailVerificationDialog
          open={true}
          user={user}
          onVerified={() => {
            // Reload the page to refresh the auth token and claims
            window.location.reload();
          }}
        />
      </>
    );
  }

  // Enforce account != access: user must be linked to org/team for core features
  // Note: hasSufficientAccess is role-aware (school admins only need org access, not team)
  if (requireAuth && user && !isSuperAdmin && !hasSufficientAccess && !isAllowedWhenUnlinked) {
    return <Navigate to="/access/setup" replace />;
  }

  if (requirePermission) {
    // Special handling for Analysis permission - use organization permission
    if (requirePermission === 'can_access_analysis') {
      if (!canAccessAiAnalysis) {
        return <Navigate to="/403" replace />;
      }
    } else if (!permissions[requirePermission]) {
      return <Navigate to="/403" replace />;
    }
  }

  return <>{children}</>;
};
